import React, { useState } from 'react'
import ColumnEditor from './ColumnEditor'
import RelationsModal from './RelationsModal'
import { useLang } from '../lib/useLang'

function TableCard({ table, isSelected, onClick, onDescriptionChange }) {
  const { t } = useLang()
  return (
    <div
      onClick={onClick}
      className={`group cursor-pointer rounded-xl p-4 transition-all duration-200 ${
        isSelected ? 'ring-1 ring-accent/30' : ''
      }`}
      style={{
        background: isSelected
          ? 'linear-gradient(135deg, rgba(255, 90, 31, 0.08) 0%, rgba(196, 61, 16, 0.04) 100%)'
          : 'rgba(14, 14, 16, 0.02)',
        border: `1px solid ${isSelected ? 'rgba(255, 90, 31, 0.2)' : 'rgba(14, 14, 16, 0.04)'}`,
      }}
      onMouseEnter={e => {
        if (!isSelected) {
          e.currentTarget.style.borderColor = 'rgba(14, 14, 16, 0.07)'
          e.currentTarget.style.background = 'rgba(14, 14, 16, 0.03)'
        }
      }}
      onMouseLeave={e => {
        if (!isSelected) {
          e.currentTarget.style.borderColor = 'rgba(14, 14, 16, 0.04)'
          e.currentTarget.style.background = 'rgba(14, 14, 16, 0.02)'
        }
      }}
    >
      <div className='mb-2 flex items-center gap-3'>
        <div
          className={`flex h-8 w-8 items-center justify-center rounded-lg transition-colors ${
            isSelected ? 'bg-accent/15' : 'bg-ink/[0.03]'
          }`}
        >
          <svg
            width='14'
            height='14'
            viewBox='0 0 24 24'
            fill='none'
            stroke='currentColor'
            strokeWidth='1.5'
            className={isSelected ? 'text-accent' : 'text-ink'}
          >
            <path d='M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z' />
            <polyline points='14,2 14,8 20,8' />
          </svg>
        </div>
        <div className='min-w-0 flex-1'>
          <div className={`truncate text-sm font-medium ${isSelected ? 'text-ink' : 'text-ink'}`}>
            {table.name}
          </div>
          <div className='text-xs text-ink'>
            {table.columns?.length || 0} {t('tableEditor.columns')}
          </div>
        </div>
        {isSelected && (
          <div className='h-2 w-2 rounded-full bg-accent/15' />
        )}
      </div>

      {/* Table Description */}
      {isSelected && (
        <div className='mt-3 pt-3' style={{ borderTop: '1px solid rgba(14, 14, 16, 0.05)' }}>
          <label className='mb-1.5 block text-[10px] font-medium uppercase tracking-wider text-ink'>
            {t('tableEditor.tableDescription')}
          </label>
          <textarea
            value={table.description || ''}
            onChange={e => {
              e.stopPropagation()
              onDescriptionChange(e.target.value)
              // Auto-resize
              e.target.style.height = 'auto'
              e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px'
            }}
            onClick={e => e.stopPropagation()}
            placeholder={t('tableEditor.descPlaceholder')}
            rows={2}
            className='mod-field w-full resize-none rounded-lg px-3 py-2 text-xs text-ink placeholder-ink/40'
            style={{ border: '1px solid rgba(14, 14, 16, 0.05)', minHeight: '48px', maxHeight: '120px', overflowY: 'auto' }}
          />
        </div>
      )}
    </div>
  )
}

export default function TableEditor({ tables, selectedTableId, onTableSelect, onTablesChange, relations, onRelationsChange }) {
  const { t } = useLang()
  const [isRelationsModalOpen, setIsRelationsModalOpen] = useState(false)

  const selectedTable = tables.find(table => table.id === selectedTableId)
  const selectedColumns = selectedTable?.columns || []

  const handleColumnsChange = newColumns => {
    if (selectedTableId == null || !onTablesChange) return
    const updatedTables = tables.map(table =>
      table.id === selectedTableId ? { ...table, columns: newColumns } : table
    )
    onTablesChange(updatedTables)
  }

  const handleDescriptionChange = (tableId, description) => {
    if (!onTablesChange) return
    const updatedTables = tables.map(table =>
      table.id === tableId ? { ...table, description } : table
    )
    onTablesChange(updatedTables)
  }

  return (
    <div className='mt-6 flex gap-6'>
      {/* Left: Tables Panel */}
      <div className='w-[260px] shrink-0'>
        <div
          className='flex h-full flex-col rounded-2xl'
          style={{
            background: 'rgba(14, 14, 16, 0.01)',
            border: '1px solid rgba(14, 14, 16, 0.04)',
          }}
        >
          {/* Header */}
          <div className='px-5 py-4' style={{ borderBottom: '1px solid rgba(14, 14, 16, 0.04)' }}>
            <h3 className='text-sm font-medium text-ink'>{t('tableEditor.tables')}</h3>
            <p className='mt-0.5 text-xs text-ink'>{t('tableEditor.filesLoaded', { count: tables.length })}</p>
          </div>

          {/* Table List */}
          <div className='flex-1 space-y-2 overflow-y-auto p-3'>
            {tables.length === 0 ? (
              <div className='flex h-32 flex-col items-center justify-center text-center'>
                <svg
                  width='24'
                  height='24'
                  viewBox='0 0 24 24'
                  fill='none'
                  stroke='currentColor'
                  strokeWidth='1.5'
                  className='mb-2 text-ink'
                >
                  <path d='M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z' />
                  <polyline points='14,2 14,8 20,8' />
                </svg>
                <p className='text-xs text-ink'>{t('tableEditor.noFiles')}</p>
              </div>
            ) : (
              tables.map(table => (
                <TableCard
                  key={table.id}
                  table={table}
                  isSelected={selectedTableId === table.id}
                  onClick={() => onTableSelect(table.id)}
                  onDescriptionChange={desc => handleDescriptionChange(table.id, desc)}
                />
              ))
            )}
          </div>

          {/* Relations Button */}
          <div className='p-3' style={{ borderTop: '1px solid rgba(14, 14, 16, 0.04)' }}>
            <button
              onClick={() => setIsRelationsModalOpen(true)}
              className='group flex w-full items-center justify-center gap-2 rounded-xl py-3 text-sm font-medium transition-all'
              style={{
                background: 'rgba(14, 14, 16, 0.02)',
                border: '1px solid rgba(14, 14, 16, 0.05)',
                color: 'rgba(14, 14, 16, 0.54)',
              }}
              onMouseEnter={e => {
                e.currentTarget.style.borderColor = 'rgba(255, 90, 31, 0.3)'
                e.currentTarget.style.color = 'rgba(255, 90, 31, 0.9)'
              }}
              onMouseLeave={e => {
                e.currentTarget.style.borderColor = 'rgba(14, 14, 16, 0.05)'
                e.currentTarget.style.color = 'rgba(14, 14, 16, 0.54)'
              }}
            >
              <svg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
                <path d='M12 5v14M5 12h14' />
              </svg>
              {t('tableEditor.addRelations')}
            </button>
          </div>
        </div>
      </div>

      {/* Right: Column Editor */}
      <div className='flex-1'>
        {selectedTableId != null && selectedTable ? (
          <ColumnEditor value={selectedColumns} onChange={handleColumnsChange} />
        ) : (
          <div
            className='flex h-64 flex-col items-center justify-center rounded-2xl'
            style={{
              background: 'rgba(14, 14, 16, 0.01)',
              border: '1px dashed rgba(14, 14, 16, 0.05)',
            }}
          >
            <svg
              width='32'
              height='32'
              viewBox='0 0 24 24'
              fill='none'
              stroke='currentColor'
              strokeWidth='1.5'
              className='mb-3 text-ink'
            >
              <rect x='3' y='3' width='7' height='7' />
              <rect x='14' y='3' width='7' height='7' />
              <rect x='14' y='14' width='7' height='7' />
              <rect x='3' y='14' width='7' height='7' />
            </svg>
            <p className='text-sm text-ink'>{t('tableEditor.selectTable')}</p>
          </div>
        )}
      </div>

      <RelationsModal
        open={isRelationsModalOpen}
        onClose={() => setIsRelationsModalOpen(false)}
        tables={tables}
        onTablesChange={onTablesChange}
        onRelationsChange={onRelationsChange}
        initialRelations={relations}
      />
    </div>
  )
}
